/**
 * API кабинета бизнеса: все 24 эндпоинта `/api/cabinet/*`.
 *
 * Поверх ТОГО ЖЕ `HttpTransport`, что и клиентская часть, — это главное решение файла.
 * Заголовок авторизации, разбор ключей ошибок и обработка сети обязаны быть одни на всё
 * приложение: заведи кабинет свой транспорт, и он получил бы вторую, чуть другую копию
 * логики входа, которая разойдётся с первой на первом же изменении.
 *
 * `auth: true` У ВСЕХ БЕЗ ИСКЛЮЧЕНИЯ. В отличие от витрины, здесь нет ни одной открытой
 * операции: роутер бэкенда вешает `requireAuth` на весь кабинет одним `router.use`, а не
 * пооперационно, — именно чтобы её нельзя было забыть в новом маршруте. Повторяем это же
 * свойство на клиенте.
 *
 * Владелец нигде не передаётся параметром. Кто спрашивает — решает подпись `initData`
 * (см. `actorTgId` в роутере бэкенда). Появись здесь `ownerTgId`, сравнение шло бы подделки
 * с подделкой, и любой читал бы любую организацию одним лишним полем.
 */

import type { HttpTransport } from './http';
import type {
  AddTimeOffInput,
  BlacklistEntryDto,
  CabinetBusinessDto,
  CalendarDayDto,
  CreateBusinessInput,
  ScheduleLevelDto,
  ServiceActiveResultDto,
  ServiceDto,
  ServiceFieldsInput,
  StaffActiveResultDto,
  StaffDto,
  TimeOffDto,
  UpdateBusinessInput,
  WorkingIntervalDto,
} from './cabinetTypes';

export interface CabinetApiClient {
  // ── Организации ──
  createBusiness(input: CreateBusinessInput): Promise<CabinetBusinessDto>;
  listMyBusinesses(): Promise<CabinetBusinessDto[]>;
  getBusiness(businessId: string): Promise<CabinetBusinessDto>;
  updateBusiness(businessId: string, patch: UpdateBusinessInput): Promise<CabinetBusinessDto>;
  publishBusiness(businessId: string): Promise<CabinetBusinessDto>;
  unpublishBusiness(businessId: string): Promise<CabinetBusinessDto>;
  /** «В историю»: не удаление, данные остаются. Отказ с будущими записями приходит ключом. */
  archiveBusiness(businessId: string): Promise<CabinetBusinessDto>;
  /** «Восстановить»: организация возвращается черновиком. */
  restoreBusiness(businessId: string): Promise<CabinetBusinessDto>;

  // ── Ограничения клиентов ──
  listBlacklist(businessId: string): Promise<BlacklistEntryDto[]>;
  /** Снять своё ограничение. Отметку неявки не трогает, глобальный бан — тем более. */
  liftBlacklistEntry(businessId: string, entryId: string): Promise<void>;

  // ── Услуги ──
  listServices(businessId: string): Promise<ServiceDto[]>;
  createService(businessId: string, fields: ServiceFieldsInput): Promise<ServiceDto>;
  updateService(
    businessId: string,
    serviceId: string,
    fields: ServiceFieldsInput,
  ): Promise<ServiceDto>;
  setServiceActive(
    businessId: string,
    serviceId: string,
    active: boolean,
  ): Promise<ServiceActiveResultDto>;

  // ── Специалисты ──
  listStaff(businessId: string): Promise<StaffDto[]>;
  createStaff(businessId: string, name: string, title: string | null): Promise<StaffDto>;
  updateStaff(
    businessId: string,
    staffId: string,
    name: string,
    title: string | null,
  ): Promise<StaffDto>;
  setStaffActive(
    businessId: string,
    staffId: string,
    active: boolean,
  ): Promise<StaffActiveResultDto>;
  /**
   * Переключить специалиста между своим графиком и часами организации.
   *
   * Отдельный метод, а не поле в `updateStaff`: там правится имя, и это косметика,
   * а здесь — источник истины для расписания. Личные часы при выключении не удаляются,
   * переключатель обратим.
   */
  setStaffOwnSchedule(
    businessId: string,
    staffId: string,
    usesOwnSchedule: boolean,
  ): Promise<StaffDto>;

  // ── Расписание ──
  /** `staffId` не задан — часы всей организации; задан — личные часы мастера. */
  getSchedule(businessId: string, staffId?: string): Promise<ScheduleLevelDto>;
  replaceSchedule(
    businessId: string,
    intervals: readonly WorkingIntervalDto[],
    staffId?: string,
  ): Promise<ScheduleLevelDto>;

  // ── Отпуска ──
  listTimeOff(
    businessId: string,
    params: { staffId: string; from: string; to: string },
  ): Promise<TimeOffDto[]>;
  addTimeOff(businessId: string, input: AddTimeOffInput): Promise<TimeOffDto>;
  removeTimeOff(businessId: string, timeOffId: string): Promise<void>;

  // ── Календарь и статусы ──
  getCalendarDay(businessId: string, date: string): Promise<CalendarDayDto>;
  /**
   * «Пришёл». На ещё не начавшемся визите бэкенд отвечает `err.completeTooEarly`:
   * это вопрос, а не отказ (ТЗ §19, №28). Повтор с `confirmedEarly` закрывает визит.
   */
  completeAppointment(
    businessId: string,
    appointmentId: string,
    options?: { confirmedEarly?: boolean },
  ): Promise<void>;
  cancelAppointment(businessId: string, appointmentId: string): Promise<void>;
  markNoShow(businessId: string, appointmentId: string): Promise<void>;
  forgiveNoShow(businessId: string, appointmentId: string): Promise<void>;

  // ── Статистика ──
  getStats(businessId: string, period: StatsPeriod): Promise<StatsDto>;

  // ── Тариф и оплата ──
  getBilling(businessId: string): Promise<BillingDto>;
  createInvoice(businessId: string): Promise<{ url: string }>;

  // ── Приглашения ──
  getReferral(businessId: string): Promise<ReferralDto>;
}

/** Реферальная ссылка и счётчики. Условия (7/14 дней, потолок) фронт берёт из `REFERRAL` в shared. */
export interface ReferralDto {
  link: string;
  /** Перешли по ссылке и засчитаны как приглашённые. */
  invited: number;
  /** Сколько приглашений уже принесли награду. */
  rewarded: number;
  /** Получено дней подписки за всё время. */
  bonusDays: number;
  /** Потолок наград на одного приглашающего. */
  maxRewarded: number;
}

export interface BillingDto {
  /** false — оплата не подключена: ни цены, ни кнопки. */
  enabled: boolean;
  plan: 'solo' | 'multi';
  /** Цена месяца в сумах; `null`, пока оплата выключена. */
  amountSum: number | null;
  isPro: boolean;
  proUntil: string | null;
  trialEndsAt: string | null;
}

export type StatsPeriod = 'day' | 'week' | 'month';

export interface StatsBucketDto {
  /** Начало периода, `YYYY-MM-DD` в поясе организации. */
  start: string;
  total: number;
  completed: number;
  noShow: number;
  cancelled: number;
  clients: number;
}

export interface StatsDto {
  period: StatsPeriod;
  tz: string;
  buckets: StatsBucketDto[];
  /** За весь диапазон: уникальные люди, постоянные (≥2 визитов) и самые частые. */
  clients: { clients: number; regulars: number; top: { name: string; visits: number }[] };
  rating: { sum: number; count: number };
}

/** Идентификаторы приходят в путь — экранируем каждый, а не склеиваем шаблоном наспех. */
const seg = (value: string): string => encodeURIComponent(value);

export function createCabinetApi(transport: HttpTransport): CabinetApiClient {
  const biz = (businessId: string): string => `/cabinet/businesses/${seg(businessId)}`;

  /**
   * Четыре кнопки статусов. Каждая — свой путь, как и на бэкенде: за одинаковым с виду
   * «сменить статус» стоят операции разной цены, и «Неявка» может забанить клиента
   * на платформе целиком. Общий метод с параметром-действием позволял бы промахнуться
   * значением там, где промах стоит клиенту бана.
   *
   * Ответ 204 без тела — отсюда `empty: true`.
   */
  const action = (businessId: string, appointmentId: string, name: string): Promise<void> =>
    transport.request<void>({
      method: 'POST',
      path: `${biz(businessId)}/appointments/${seg(appointmentId)}/${name}`,
      auth: true,
      empty: true,
    });

  return {
    createBusiness: (input) =>
      transport.request<CabinetBusinessDto>({
        method: 'POST',
        path: '/cabinet/businesses',
        body: input,
        auth: true,
      }),

    listMyBusinesses: () =>
      transport.request<CabinetBusinessDto[]>({
        method: 'GET',
        path: '/cabinet/businesses',
        auth: true,
      }),

    getBusiness: (businessId) =>
      transport.request<CabinetBusinessDto>({ method: 'GET', path: biz(businessId), auth: true }),

    /**
     * Патч. `slug` и `type` в него не входят и бэкендом ОТВЕРГАЮТСЯ, а не игнорируются:
     * молча проглоченный `slug` дал бы владельцу уверенность, что публичный адрес сменился,
     * когда на QR-наклейках у клиентов остался старый.
     */
    updateBusiness: (businessId, patch) =>
      transport.request<CabinetBusinessDto>({
        method: 'PATCH',
        path: biz(businessId),
        body: patch,
        auth: true,
      }),

    publishBusiness: (businessId) =>
      transport.request<CabinetBusinessDto>({
        method: 'POST',
        path: `${biz(businessId)}/publish`,
        auth: true,
      }),

    unpublishBusiness: (businessId) =>
      transport.request<CabinetBusinessDto>({
        method: 'POST',
        path: `${biz(businessId)}/unpublish`,
        auth: true,
      }),

    archiveBusiness: (businessId) =>
      transport.request<CabinetBusinessDto>({
        method: 'POST',
        path: `${biz(businessId)}/archive`,
        auth: true,
      }),

    restoreBusiness: (businessId) =>
      transport.request<CabinetBusinessDto>({
        method: 'POST',
        path: `${biz(businessId)}/restore`,
        auth: true,
      }),

    listBlacklist: async (businessId) => {
      const body = await transport.request<{ entries: BlacklistEntryDto[] }>({
        method: 'GET',
        path: `${biz(businessId)}/blacklist`,
        auth: true,
      });
      return body.entries;
    },

    liftBlacklistEntry: async (businessId, entryId) => {
      await transport.request<void>({
        method: 'DELETE',
        path: `${biz(businessId)}/blacklist/${entryId}`,
        auth: true,
      });
    },

    listServices: (businessId) =>
      transport.request<ServiceDto[]>({
        method: 'GET',
        path: `${biz(businessId)}/services`,
        auth: true,
      }),

    createService: (businessId, fields) =>
      transport.request<ServiceDto>({
        method: 'POST',
        path: `${biz(businessId)}/services`,
        body: fields,
        auth: true,
      }),

    updateService: (businessId, serviceId, fields) =>
      transport.request<ServiceDto>({
        method: 'PATCH',
        path: `${biz(businessId)}/services/${seg(serviceId)}`,
        body: fields,
        auth: true,
      }),

    setServiceActive: (businessId, serviceId, active) =>
      transport.request<ServiceActiveResultDto>({
        method: 'PATCH',
        path: `${biz(businessId)}/services/${seg(serviceId)}/active`,
        body: { active },
        auth: true,
      }),

    listStaff: (businessId) =>
      transport.request<StaffDto[]>({
        method: 'GET',
        path: `${biz(businessId)}/staff`,
        auth: true,
      }),

    createStaff: (businessId, name, title) =>
      transport.request<StaffDto>({
        method: 'POST',
        path: `${biz(businessId)}/staff`,
        body: { name, title },
        auth: true,
      }),

    updateStaff: (businessId, staffId, name, title) =>
      transport.request<StaffDto>({
        method: 'PATCH',
        path: `${biz(businessId)}/staff/${seg(staffId)}`,
        body: { name, title },
        auth: true,
      }),

    setStaffActive: (businessId, staffId, active) =>
      transport.request<StaffActiveResultDto>({
        method: 'PATCH',
        path: `${biz(businessId)}/staff/${seg(staffId)}/active`,
        body: { active },
        auth: true,
      }),

    setStaffOwnSchedule: (businessId, staffId, usesOwnSchedule) =>
      transport.request<StaffDto>({
        method: 'PATCH',
        path: `${biz(businessId)}/staff/${seg(staffId)}/own-schedule`,
        body: { usesOwnSchedule },
        auth: true,
      }),

    getSchedule: (businessId, staffId) =>
      transport.request<ScheduleLevelDto>({
        method: 'GET',
        path: `${biz(businessId)}/schedule`,
        query: { staffId },
        auth: true,
      }),

    /**
     * PUT, а не PATCH: уровень ЗАМЕНЯЕТСЯ целиком. Пересечение интервалов — свойство пары,
     * проверить его можно только имея набор целиком, поэтому построчной правки не существует.
     * Пустой массив законен: для организации это «закрыта», для мастера — снятие личного
     * графика и возврат к часам организации.
     *
     * Уровень задаётся ТОЛЬКО query. `staffId` в теле бэкенд отвергает: проглоченный, он
     * заменил бы часы всей организации вместо личных часов мастера — то есть стёр бы
     * расписание, а не ошибся полем в ответе.
     */
    replaceSchedule: (businessId, intervals, staffId) =>
      transport.request<ScheduleLevelDto>({
        method: 'PUT',
        path: `${biz(businessId)}/schedule`,
        query: { staffId },
        body: { intervals },
        auth: true,
      }),

    listTimeOff: (businessId, params) =>
      transport.request<TimeOffDto[]>({
        method: 'GET',
        path: `${biz(businessId)}/time-off`,
        query: { staffId: params.staffId, from: params.from, to: params.to },
        auth: true,
      }),

    addTimeOff: (businessId, input) =>
      transport.request<TimeOffDto>({
        method: 'POST',
        path: `${biz(businessId)}/time-off`,
        body: input,
        auth: true,
      }),

    removeTimeOff: (businessId, timeOffId) =>
      transport.request<void>({
        method: 'DELETE',
        path: `${biz(businessId)}/time-off/${seg(timeOffId)}`,
        auth: true,
        empty: true,
      }),

    /**
     * День календаря. `date` — строго `YYYY-MM-DD` в поясе ОРГАНИЗАЦИИ, не момент времени:
     * границу суток разворачивает сервер, у которого есть `businesses.tz`. Пришли мы момент
     * или посчитай день по часам телефона — ташкентское утро в UTC оказалось бы вчерашним
     * днём, и половина записей пропала бы с экрана (AGENTS.md, правило 1).
     */
    getCalendarDay: (businessId, date) =>
      transport.request<CalendarDayDto>({
        method: 'GET',
        path: `${biz(businessId)}/calendar`,
        query: { date },
        auth: true,
      }),

    completeAppointment: (businessId, appointmentId, options) =>
      // Флаг строкой запроса: у действия нет тела, а общий `action` его не знает.
      action(
        businessId,
        appointmentId,
        options?.confirmedEarly ? 'complete?confirmedEarly=true' : 'complete',
      ),
    cancelAppointment: (businessId, appointmentId) => action(businessId, appointmentId, 'cancel'),
    markNoShow: (businessId, appointmentId) => action(businessId, appointmentId, 'no-show'),
    forgiveNoShow: (businessId, appointmentId) => action(businessId, appointmentId, 'forgive'),

    getStats: (businessId, period) =>
      transport.request<StatsDto>({
        method: 'GET',
        path: `${biz(businessId)}/stats`,
        query: { period },
        auth: true,
      }),

    getBilling: (businessId) =>
      transport.request<BillingDto>({ method: 'GET', path: `${biz(businessId)}/billing`, auth: true }),
    createInvoice: (businessId) =>
      transport.request<{ url: string }>({ method: 'POST', path: `${biz(businessId)}/invoice`, auth: true }),

    getReferral: (businessId) =>
      transport.request<ReferralDto>({ method: 'GET', path: `${biz(businessId)}/referral`, auth: true }),
  };
}
